import { useState, useRef, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import AdminLayout from "@/components/layout-admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DeleteConfirmDialog } from "@/components/ui/delete-confirm-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import {
  Zap, Plus, Play, Pause, Trash2, Settings, Clock, CheckCircle2, XCircle,
  ArrowRight, CalendarCheck, UserCheck, Bell, Mail, MessageSquare, ClipboardList,
  Sparkles, Edit, Activity, AlertTriangle, ChevronDown, ChevronRight, Eye,
  TestTube2, RefreshCw, Filter, Webhook, Copy, Key, DollarSign, Wrench,
  FileText, Send, Search, Globe, StickyNote, CalendarX2, MessageCircle,
  ClipboardCheck, UserPlus, PlayCircle,
} from "lucide-react";
import type { AutomationRule, AutomationRun } from "@shared/schema";

const triggerTypes = [
  { value: "reservation_created", label: "Reservation Created", icon: CalendarCheck, description: "When a new reservation is created" },
  { value: "reservation_updated", label: "Reservation Updated", icon: Edit, description: "When a reservation is modified" },
  { value: "reservation_cancelled", label: "Reservation Cancelled", icon: CalendarX2, description: "When a reservation is cancelled" },
  { value: "reservation_checkin", label: "Guest Check-in", icon: UserCheck, description: "When a guest checks in" },
  { value: "reservation_checkout", label: "Guest Check-out", icon: UserCheck, description: "When a guest checks out" },
  { value: "payment_received", label: "Payment Received", icon: DollarSign, description: "When a payment is received" },
  { value: "ticket_created", label: "Ticket Created", icon: ClipboardList, description: "When a support ticket is created" },
  { value: "cleaning_job_created", label: "Cleaning Job Created", icon: ClipboardCheck, description: "When a new cleaning job is created" },
  { value: "cleaning_job_assigned", label: "Cleaning Job Assigned", icon: UserPlus, description: "When a cleaner is assigned to a job" },
  { value: "cleaning_job_started", label: "Cleaning Job Started", icon: PlayCircle, description: "When a cleaner starts a cleaning job" },
  { value: "cleaning_completed", label: "Cleaning Completed", icon: Sparkles, description: "When a cleaning job is completed" },
  { value: "maintenance_reported", label: "Maintenance Reported", icon: AlertTriangle, description: "When maintenance is reported" },
  { value: "guest_message_received", label: "Guest Message", icon: MessageCircle, description: "When a guest sends a message" },
  { value: "financial_record_created", label: "Financial Record Created", icon: FileText, description: "When a financial record is created" },
  { value: "webhook_received", label: "Webhook Received", icon: Globe, description: "Triggered by an external webhook" },
  { value: "schedule", label: "Scheduled", icon: Clock, description: "Runs on a schedule (daily, weekly)" },
];

const triggerVariables: Record<string, { name: string; description: string }[]> = {
  reservation_created: [
    { name: "guestName", description: "Guest full name" },
    { name: "guestEmail", description: "Guest email address" },
    { name: "guestPhone", description: "Guest phone number" },
    { name: "apartmentName", description: "Apartment name" },
    { name: "apartmentId", description: "Apartment ID" },
    { name: "checkInDate", description: "Check-in date" },
    { name: "checkOutDate", description: "Check-out date" },
    { name: "reservationId", description: "Reservation ID" },
    { name: "status", description: "Reservation status" },
    { name: "source", description: "Booking source" },
    { name: "totalAmount", description: "Total amount" },
    { name: "ownerUserId", description: "Property manager user ID" },
  ],
  reservation_updated: [
    { name: "guestName", description: "Guest full name" },
    { name: "guestEmail", description: "Guest email address" },
    { name: "apartmentName", description: "Apartment name" },
    { name: "apartmentId", description: "Apartment ID" },
    { name: "checkInDate", description: "Check-in date" },
    { name: "checkOutDate", description: "Check-out date" },
    { name: "reservationId", description: "Reservation ID" },
    { name: "status", description: "Reservation status" },
    { name: "ownerUserId", description: "Property manager user ID" },
  ],
  reservation_cancelled: [
    { name: "guestName", description: "Guest full name" },
    { name: "guestEmail", description: "Guest email address" },
    { name: "apartmentName", description: "Apartment name" },
    { name: "reservationId", description: "Reservation ID" },
    { name: "checkInDate", description: "Check-in date" },
    { name: "checkOutDate", description: "Check-out date" },
    { name: "ownerUserId", description: "Property manager user ID" },
  ],
  reservation_checkin: [
    { name: "guestName", description: "Guest full name" },
    { name: "guestEmail", description: "Guest email address" },
    { name: "apartmentName", description: "Apartment name" },
    { name: "apartmentId", description: "Apartment ID" },
    { name: "checkInDate", description: "Check-in date" },
    { name: "checkOutDate", description: "Check-out date" },
    { name: "reservationId", description: "Reservation ID" },
  ],
  reservation_checkout: [
    { name: "guestName", description: "Guest full name" },
    { name: "guestEmail", description: "Guest email address" },
    { name: "apartmentName", description: "Apartment name" },
    { name: "apartmentId", description: "Apartment ID" },
    { name: "reservationId", description: "Reservation ID" },
    { name: "checkOutDate", description: "Check-out date" },
  ],
  payment_received: [
    { name: "guestName", description: "Guest full name" },
    { name: "apartmentName", description: "Apartment name" },
    { name: "amount", description: "Payment amount" },
    { name: "paymentMethod", description: "Payment method" },
    { name: "reservationId", description: "Reservation ID" },
  ],
  ticket_created: [
    { name: "subject", description: "Ticket subject" },
    { name: "category", description: "Ticket category" },
    { name: "priority", description: "Ticket priority" },
    { name: "ticketId", description: "Ticket ID" },
  ],
  cleaning_job_created: [
    { name: "apartmentName", description: "Apartment name" },
    { name: "apartmentId", description: "Apartment ID" },
    { name: "cleaningJobId", description: "Cleaning job ID" },
    { name: "priority", description: "Job priority" },
    { name: "scheduledDate", description: "Scheduled date" },
    { name: "notes", description: "Job notes" },
    { name: "assignedTo", description: "Assigned cleaner user ID" },
    { name: "ownerUserId", description: "Property manager user ID" },
  ],
  cleaning_job_assigned: [
    { name: "apartmentName", description: "Apartment name" },
    { name: "apartmentId", description: "Apartment ID" },
    { name: "cleaningJobId", description: "Cleaning job ID" },
    { name: "cleanerName", description: "Assigned cleaner name" },
    { name: "assignedTo", description: "Assigned cleaner user ID" },
    { name: "priority", description: "Job priority" },
    { name: "ownerUserId", description: "Property manager user ID" },
  ],
  cleaning_job_started: [
    { name: "apartmentName", description: "Apartment name" },
    { name: "apartmentId", description: "Apartment ID" },
    { name: "cleaningJobId", description: "Cleaning job ID" },
    { name: "cleanerName", description: "Cleaner name" },
    { name: "assignedTo", description: "Cleaner user ID" },
    { name: "ownerUserId", description: "Property manager user ID" },
  ],
  cleaning_completed: [
    { name: "apartmentName", description: "Apartment name" },
    { name: "apartmentId", description: "Apartment ID" },
    { name: "cleanerName", description: "Cleaner name" },
    { name: "cleaningJobId", description: "Cleaning job ID" },
    { name: "assignedTo", description: "Assigned cleaner user ID" },
    { name: "ownerUserId", description: "Property manager user ID" },
  ],
  maintenance_reported: [
    { name: "apartmentName", description: "Apartment name" },
    { name: "apartmentId", description: "Apartment ID" },
    { name: "title", description: "Issue title" },
    { name: "priority", description: "Issue priority" },
    { name: "assignedToUserId", description: "Assigned maintenance user ID" },
    { name: "ownerUserId", description: "Property manager user ID" },
  ],
  guest_message_received: [
    { name: "guestName", description: "Guest full name" },
    { name: "message", description: "Message content" },
    { name: "channel", description: "Message channel" },
    { name: "conversationId", description: "Conversation ID" },
  ],
  financial_record_created: [
    { name: "type", description: "Record type (income/expense)" },
    { name: "category", description: "Financial category" },
    { name: "amount", description: "Amount" },
    { name: "description", description: "Description" },
    { name: "apartmentName", description: "Apartment name" },
  ],
  webhook_received: [
    { name: "webhookSource", description: "Webhook caller user-agent" },
    { name: "receivedAt", description: "Timestamp received" },
  ],
  schedule: [
    { name: "scheduledAt", description: "Scheduled run time" },
  ],
};

const actionTypes = [
  { value: "send_email", label: "Send Email", icon: Mail, configFields: [
    { key: "recipient", label: "Recipient Email", placeholder: "guest@example.com or {{guestEmail}}" },
    { key: "subject", label: "Subject", placeholder: "Welcome to your stay!" },
    { key: "body", label: "Email Body", placeholder: "Dear {{guestName}}, welcome...", multiline: true },
  ]},
  { value: "send_notification", label: "Send Notification", icon: Bell, configFields: [
    { key: "title", label: "Notification Title", placeholder: "New reservation alert" },
    { key: "body", label: "Notification Body", placeholder: "A new reservation has been created for {{apartmentName}}", multiline: true },
    { key: "recipientRole", label: "Send To", type: "select", options: [
      { value: "admin", label: "All Admins" },
      { value: "cleaner", label: "All Cleaners" },
      { value: "maintenance", label: "All Maintenance" },
      { value: "owner", label: "All Property Managers" },
      { value: "assigned_cleaner", label: "Assigned Cleaner (from trigger)" },
      { value: "assigned_owner", label: "Assigned Property Manager (from trigger)" },
      { value: "assigned_maintenance", label: "Assigned Maintenance (from trigger)" },
    ]},
  ]},
  { value: "send_sms", label: "Send SMS", icon: MessageSquare, configFields: [
    { key: "to", label: "Phone Number", placeholder: "+1 555 123 4567 or {{guestPhone}}" },
    { key: "body", label: "SMS Body", placeholder: "Hi {{guestName}}, your reservation is confirmed!", multiline: true },
  ]},
  { value: "create_ticket", label: "Create Ticket", icon: ClipboardList, configFields: [
    { key: "subject", label: "Ticket Subject", placeholder: "Follow-up needed for {{guestName}}" },
    { key: "body", label: "Ticket Description", placeholder: "Auto-created ticket details...", multiline: true },
    { key: "category", label: "Category", type: "select", options: [
      { value: "general", label: "General" }, { value: "maintenance", label: "Maintenance" },
      { value: "cleaning", label: "Cleaning" }, { value: "billing", label: "Billing" },
    ]},
    { key: "priority", label: "Priority", type: "select", options: [
      { value: "low", label: "Low" }, { value: "normal", label: "Normal" },
      { value: "high", label: "High" }, { value: "urgent", label: "Urgent" },
    ]},
  ]},
  { value: "assign_cleaner", label: "Assign Cleaner", icon: Sparkles, configFields: [
    { key: "cleanerId", label: "Cleaner Assignment", placeholder: "Leave empty for auto round-robin" },
  ]},
  { value: "update_status", label: "Update Status", icon: Settings, configFields: [
    { key: "newStatus", label: "New Status", type: "select", options: [
      { value: "confirmed", label: "Confirmed" }, { value: "pending", label: "Pending" },
      { value: "completed", label: "Completed" }, { value: "cancelled", label: "Cancelled" },
    ]},
  ]},
  { value: "send_webhook", label: "Send Webhook", icon: Globe, configFields: [
    { key: "url", label: "Webhook URL", placeholder: "https://example.com/webhook" },
    { key: "method", label: "HTTP Method", type: "select", options: [
      { value: "POST", label: "POST" }, { value: "PUT", label: "PUT" }, { value: "PATCH", label: "PATCH" },
    ]},
    { key: "headers", label: "Custom Headers (JSON)", placeholder: '{"Authorization": "Bearer xxx"}', multiline: true },
    { key: "bodyTemplate", label: "Body Template (JSON)", placeholder: '{"event": "{{triggerType}}", "guest": "{{guestName}}"}', multiline: true },
  ]},
  { value: "create_cleaning_job", label: "Create Cleaning Job", icon: Sparkles, configFields: [
    { key: "apartmentId", label: "Apartment ID", placeholder: "{{apartmentId}}" },
    { key: "priority", label: "Priority", type: "select", options: [
      { value: "low", label: "Low" }, { value: "normal", label: "Normal" },
      { value: "high", label: "High" }, { value: "urgent", label: "Urgent" },
    ]},
    { key: "notes", label: "Notes", placeholder: "Checkout cleaning for {{guestName}}", multiline: true },
  ]},
  { value: "create_financial_record", label: "Create Financial Record", icon: DollarSign, configFields: [
    { key: "type", label: "Type", type: "select", options: [
      { value: "income", label: "Income" }, { value: "expense", label: "Expense" },
    ]},
    { key: "category", label: "Category", placeholder: "cleaning, commission, etc." },
    { key: "amount", label: "Amount", placeholder: "{{amount}} or fixed amount" },
    { key: "description", label: "Description", placeholder: "Auto-generated record for {{guestName}}", multiline: true },
    { key: "apartmentId", label: "Apartment ID", placeholder: "{{apartmentId}}" },
  ]},
  { value: "add_note_to_reservation", label: "Add Note to Reservation", icon: StickyNote, configFields: [
    { key: "note", label: "Note Text", placeholder: "Auto-note: {{guestName}} checked in at {{checkInDate}}", multiline: true },
  ]},
  { value: "send_guest_message", label: "Send Guest Message", icon: Send, configFields: [
    { key: "message", label: "Message", placeholder: "Welcome {{guestName}}! Your apartment {{apartmentName}} is ready.", multiline: true },
    { key: "channel", label: "Channel", type: "select", options: [
      { value: "platform", label: "Platform Chat" }, { value: "email", label: "Email" },
      { value: "sms", label: "SMS" }, { value: "whatsapp", label: "WhatsApp" },
    ]},
  ]},
  { value: "update_reservation", label: "Update Reservation", icon: CalendarCheck, configFields: [
    { key: "status", label: "New Status", type: "select", options: [
      { value: "confirmed", label: "Confirmed" }, { value: "pending", label: "Pending" },
      { value: "checked_in", label: "Checked In" }, { value: "checked_out", label: "Checked Out" },
      { value: "cancelled", label: "Cancelled" },
    ]},
    { key: "notes", label: "Append Notes", placeholder: "Status auto-updated by automation", multiline: true },
  ]},
  { value: "assign_maintenance", label: "Assign Maintenance", icon: Wrench, configFields: [
    { key: "userId", label: "Maintenance User ID", placeholder: "Leave empty for auto-assign" },
    { key: "priority", label: "Priority", type: "select", options: [
      { value: "low", label: "Low" }, { value: "normal", label: "Normal" },
      { value: "high", label: "High" }, { value: "urgent", label: "Urgent" },
    ]},
  ]},
];

interface RuleFormState {
  name: string;
  description: string;
  triggerType: string;
  conditions: Record<string, string>;
  actions: { type: string; config: Record<string, string> }[];
  enabled: boolean;
}

const emptyForm: RuleFormState = {
  name: "", description: "", triggerType: "",
  conditions: {}, actions: [], enabled: true,
};

const conditionFields: Record<string, { key: string; label: string; placeholder: string }[]> = {
  reservation_created: [
    { key: "source", label: "Booking Source", placeholder: "e.g., direct, airbnb, booking.com" },
    { key: "status", label: "Status", placeholder: "e.g., confirmed" },
  ],
  reservation_updated: [
    { key: "status", label: "Status", placeholder: "e.g., confirmed, pending" },
    { key: "apartmentId", label: "Apartment ID", placeholder: "Specific apartment" },
  ],
  reservation_cancelled: [
    { key: "apartmentId", label: "Apartment ID", placeholder: "Specific apartment" },
  ],
  reservation_checkout: [
    { key: "apartmentId", label: "Apartment ID", placeholder: "Specific apartment" },
  ],
  reservation_checkin: [
    { key: "apartmentId", label: "Apartment ID", placeholder: "Specific apartment" },
  ],
  ticket_created: [
    { key: "category", label: "Category", placeholder: "e.g., maintenance, cleaning" },
    { key: "priority", label: "Priority", placeholder: "e.g., high, urgent" },
  ],
  cleaning_job_created: [
    { key: "apartmentId", label: "Apartment ID", placeholder: "Specific apartment" },
    { key: "priority", label: "Priority", placeholder: "e.g., urgent, normal, low" },
  ],
  cleaning_job_assigned: [
    { key: "apartmentId", label: "Apartment ID", placeholder: "Specific apartment" },
  ],
  cleaning_job_started: [
    { key: "apartmentId", label: "Apartment ID", placeholder: "Specific apartment" },
  ],
  cleaning_completed: [
    { key: "apartmentId", label: "Apartment ID", placeholder: "Specific apartment" },
  ],
  maintenance_reported: [
    { key: "priority", label: "Priority", placeholder: "e.g., high, urgent" },
  ],
  payment_received: [
    { key: "paymentMethod", label: "Payment Method", placeholder: "e.g., cash, card" },
  ],
  guest_message_received: [
    { key: "channel", label: "Channel", placeholder: "e.g., platform, email, sms" },
  ],
  financial_record_created: [
    { key: "type", label: "Record Type", placeholder: "e.g., income, expense" },
    { key: "category", label: "Category", placeholder: "e.g., rent, cleaning" },
  ],
  webhook_received: [
    { key: "webhookSource", label: "Source User-Agent", placeholder: "Filter by webhook caller" },
  ],
};

function VariablePills({ triggerType, onInsert }: { triggerType: string; onInsert: (v: string) => void }) {
  const vars = triggerVariables[triggerType];
  if (!vars || vars.length === 0) return null;

  return (
    <div className="mt-1">
      <p className="text-[10px] text-muted-foreground mb-1">Click to insert variable:</p>
      <div className="flex flex-wrap gap-1">
        {vars.map(v => (
          <button
            key={v.name}
            type="button"
            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono bg-orange-50 dark:bg-orange-950 text-[#C2652A] border border-[#C2652A]/20 hover:bg-orange-100 dark:hover:bg-orange-900 transition-colors cursor-pointer"
            onClick={() => onInsert(`{{${v.name}}}`)}
            title={v.description}
            data-testid={`var-pill-${v.name}`}
          >
            {`{{${v.name}}}`}
          </button>
        ))}
      </div>
    </div>
  );
}

function ActionConfigForm({ action, index, onChange, triggerType }: {
  action: { type: string; config: Record<string, string> };
  index: number;
  onChange: (config: Record<string, string>) => void;
  triggerType: string;
}) {
  const actionDef = actionTypes.find(a => a.value === action.type);
  if (!actionDef?.configFields) return null;

  const activeFieldRef = useRef<string | null>(null);

  const insertVariable = useCallback((variable: string) => {
    const fieldKey = activeFieldRef.current;
    if (!fieldKey) {
      const firstTextField = actionDef.configFields.find(f => !(f as any).type || (f as any).type !== "select");
      if (firstTextField) {
        onChange({ ...action.config, [firstTextField.key]: (action.config[firstTextField.key] || "") + variable });
      }
      return;
    }
    onChange({ ...action.config, [fieldKey]: (action.config[fieldKey] || "") + variable });
  }, [action.config, onChange, actionDef.configFields]);

  return (
    <div className="space-y-2 pl-4 border-l-2 border-[#C2652A]/20 mt-2">
      {actionDef.configFields.map(field => (
        <div key={field.key}>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">{field.label}</label>
          {(field as any).type === "select" ? (
            <Select value={action.config[field.key] || ""} onValueChange={v => onChange({ ...action.config, [field.key]: v })}>
              <SelectTrigger className="h-8 text-xs" data-testid={`action-config-${index}-${field.key}`}>
                <SelectValue placeholder={`Select ${field.label.toLowerCase()}...`} />
              </SelectTrigger>
              <SelectContent>
                {((field as any).options || []).map((opt: { value: string; label: string }) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (field as any).multiline ? (
            <Textarea
              className="text-xs min-h-[60px]"
              placeholder={field.placeholder}
              value={action.config[field.key] || ""}
              onChange={e => onChange({ ...action.config, [field.key]: e.target.value })}
              onFocus={() => { activeFieldRef.current = field.key; }}
              data-testid={`action-config-${index}-${field.key}`}
            />
          ) : (
            <Input
              className="h-8 text-xs"
              placeholder={field.placeholder}
              value={action.config[field.key] || ""}
              onChange={e => onChange({ ...action.config, [field.key]: e.target.value })}
              onFocus={() => { activeFieldRef.current = field.key; }}
              data-testid={`action-config-${index}-${field.key}`}
            />
          )}
        </div>
      ))}
      <VariablePills triggerType={triggerType} onInsert={insertVariable} />
    </div>
  );
}

function RuleFormDialog({ open, onOpenChange, initialData, onSubmit, isPending, title }: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  initialData: RuleFormState;
  onSubmit: (data: RuleFormState) => void;
  isPending: boolean;
  title: string;
}) {
  const [form, setForm] = useState<RuleFormState>(initialData);
  const [showConditions, setShowConditions] = useState(Object.keys(initialData.conditions || {}).length > 0);
  const [triggerSearch, setTriggerSearch] = useState("");

  const addAction = () => setForm(f => ({ ...f, actions: [...f.actions, { type: "", config: {} }] }));
  const removeAction = (i: number) => setForm(f => ({ ...f, actions: f.actions.filter((_, idx) => idx !== i) }));
  const moveAction = (i: number, dir: -1 | 1) => {
    setForm(f => {
      const actions = [...f.actions];
      const target = i + dir;
      if (target < 0 || target >= actions.length) return f;
      [actions[i], actions[target]] = [actions[target], actions[i]];
      return { ...f, actions };
    });
  };
  const updateActionType = (i: number, type: string) => {
    setForm(f => {
      const actions = [...f.actions];
      actions[i] = { type, config: {} };
      return { ...f, actions };
    });
  };
  const updateActionConfig = (i: number, config: Record<string, string>) => {
    setForm(f => {
      const actions = [...f.actions];
      actions[i] = { ...actions[i], config };
      return { ...f, actions };
    });
  };

  const filteredTriggers = triggerSearch
    ? triggerTypes.filter(t => t.label.toLowerCase().includes(triggerSearch.toLowerCase()) || t.description.toLowerCase().includes(triggerSearch.toLowerCase()))
    : triggerTypes;

  return (
    <Dialog open={open} onOpenChange={v => { onOpenChange(v); if (v) { setForm(initialData); setTriggerSearch(""); } }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Configure the trigger, conditions, and actions for this automation</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <label className="text-sm font-medium mb-1 block">Rule Name *</label>
            <Input placeholder="e.g., Welcome email on check-in" value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="input-rule-name" />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Description</label>
            <Textarea placeholder="What does this rule do?" value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="input-rule-description" />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Trigger *</label>
            <Input
              className="mb-2 h-8 text-xs"
              placeholder="Search triggers..."
              value={triggerSearch}
              onChange={e => setTriggerSearch(e.target.value)}
              data-testid="input-trigger-search"
            />
            <div className="grid grid-cols-1 gap-1.5 max-h-[240px] overflow-y-auto pr-1">
              {filteredTriggers.map(trigger => {
                const Icon = trigger.icon;
                return (
                  <button key={trigger.value}
                    className={`flex items-center gap-3 p-2.5 rounded-lg border text-left transition-all ${
                      form.triggerType === trigger.value
                        ? "border-[#C2652A] bg-orange-50 dark:bg-orange-950"
                        : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
                    }`}
                    onClick={() => setForm(f => ({ ...f, triggerType: trigger.value }))}
                    data-testid={`trigger-${trigger.value}`}
                  >
                    <div className={`h-7 w-7 rounded-md flex items-center justify-center flex-shrink-0 ${
                      form.triggerType === trigger.value ? "bg-[#C2652A] text-white" : "bg-gray-100 dark:bg-gray-800 text-gray-500"
                    }`}>
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-xs">{trigger.label}</p>
                      <p className="text-[10px] text-gray-500 truncate">{trigger.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {form.triggerType && triggerVariables[form.triggerType] && (
            <div className="bg-orange-50/50 dark:bg-orange-950/30 rounded-lg p-3 border border-[#C2652A]/10">
              <p className="text-xs font-medium text-[#C2652A] mb-1.5 flex items-center gap-1">
                <Zap className="h-3 w-3" /> Available Variables
              </p>
              <div className="flex flex-wrap gap-1">
                {triggerVariables[form.triggerType].map(v => (
                  <span key={v.name} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono bg-white dark:bg-gray-900 border border-[#C2652A]/15 text-[#C2652A]" title={v.description}>
                    {`{{${v.name}}}`}
                  </span>
                ))}
              </div>
            </div>
          )}

          {form.triggerType && conditionFields[form.triggerType] && (
            <div>
              <button className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                onClick={() => setShowConditions(!showConditions)} data-testid="toggle-conditions">
                {showConditions ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <Filter className="h-3.5 w-3.5" />
                Conditions (optional)
              </button>
              {showConditions && (
                <div className="space-y-2 mt-2 pl-4 border-l-2 border-blue-200 dark:border-blue-800">
                  <p className="text-xs text-muted-foreground">Only run when these conditions match:</p>
                  {conditionFields[form.triggerType].map(field => (
                    <div key={field.key}>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">{field.label}</label>
                      <Input className="h-8 text-xs" placeholder={field.placeholder}
                        value={(form.conditions as any)?.[field.key] || ""}
                        onChange={e => setForm(f => ({
                          ...f,
                          conditions: { ...(f.conditions as any), [field.key]: e.target.value || undefined }
                        }))}
                        data-testid={`condition-${field.key}`} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <Separator />

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">Actions * <span className="text-xs text-muted-foreground font-normal ml-1">({form.actions.length} action{form.actions.length !== 1 ? "s" : ""})</span></label>
              <Button variant="outline" size="sm" onClick={addAction} data-testid="add-action-btn">
                <Plus className="h-3 w-3 mr-1" /> Add Action
              </Button>
            </div>
            {form.actions.length === 0 ? (
              <div className="text-center p-4 border border-dashed rounded-lg">
                <p className="text-sm text-gray-500">No actions added yet. Add at least one action.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {form.actions.map((action, i) => {
                  const actionDef = actionTypes.find(a => a.value === action.type);
                  return (
                    <div key={i} className="p-3 border rounded-lg space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="flex flex-col gap-0.5 mr-1">
                          <button className="text-muted-foreground hover:text-foreground disabled:opacity-30 text-[10px] leading-none" disabled={i === 0}
                            onClick={() => moveAction(i, -1)} title="Move up" data-testid={`move-up-${i}`}>&#9650;</button>
                          <span className="text-[10px] font-medium text-muted-foreground text-center">{i + 1}</span>
                          <button className="text-muted-foreground hover:text-foreground disabled:opacity-30 text-[10px] leading-none" disabled={i === form.actions.length - 1}
                            onClick={() => moveAction(i, 1)} title="Move down" data-testid={`move-down-${i}`}>&#9660;</button>
                        </div>
                        <Select value={action.type} onValueChange={v => updateActionType(i, v)}>
                          <SelectTrigger className="flex-1 h-9 text-sm" data-testid={`action-select-${i}`}>
                            <SelectValue placeholder="Select action type..." />
                          </SelectTrigger>
                          <SelectContent>
                            {actionTypes.map(at => (
                              <SelectItem key={at.value} value={at.value}>
                                <span className="flex items-center gap-2">
                                  <at.icon className="h-3 w-3" /> {at.label}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button variant="ghost" size="sm" onClick={() => removeAction(i)} data-testid={`remove-action-${i}`}>
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                      {action.type && (
                        <ActionConfigForm action={action} index={i}
                          onChange={config => updateActionConfig(i, config)}
                          triggerType={form.triggerType} />
                      )}
                    </div>
                  );
                })}
                {form.actions.length > 0 && (
                  <p className="text-[10px] text-muted-foreground text-center">Actions execute in order from top to bottom</p>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between">
            <label className="text-sm font-medium">Enabled</label>
            <Switch checked={form.enabled} onCheckedChange={v => setForm(f => ({ ...f, enabled: v }))} data-testid="toggle-enabled" />
          </div>

          <Button className="w-full bg-[#C2652A] hover:bg-[#A8531E]"
            disabled={!form.name || !form.triggerType || form.actions.length === 0 || isPending}
            onClick={() => onSubmit(form)} data-testid="submit-rule-btn">
            {isPending ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
            {title.includes("Edit") ? "Save Changes" : "Create Rule"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WebhookTokenSection() {
  const { toast } = useToast();
  const [showToken, setShowToken] = useState(false);

  const { data: tokenData } = useQuery<{ webhookToken: string | null }>({
    queryKey: ["/api/admin/webhook-token"],
  });

  const generateToken = useMutation({
    mutationFn: () => apiRequest("POST", "/api/admin/webhook-token/generate"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/webhook-token"] });
      toast({ title: "Webhook token generated" });
    },
  });

  const token = tokenData?.webhookToken;
  const webhookUrl = token ? `${window.location.origin}/api/webhooks/incoming/${token}` : null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Key className="h-4 w-4 text-[#C2652A]" />
          Incoming Webhook
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          Use this URL to receive webhooks from external services and trigger automations.
        </p>
        {token ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Input
                className="text-xs font-mono h-8"
                readOnly
                value={showToken ? (webhookUrl || "") : "••••••••••••••••••••••"}
                data-testid="webhook-url-input"
              />
              <Button variant="outline" size="sm" onClick={() => setShowToken(!showToken)} data-testid="toggle-show-token">
                <Eye className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => {
                navigator.clipboard.writeText(webhookUrl || "");
                toast({ title: "Copied to clipboard" });
              }} data-testid="copy-webhook-url">
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground">
              POST JSON to this URL. The payload will be available in webhook_received trigger variables.
            </p>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={() => generateToken.mutate()}
            disabled={generateToken.isPending} data-testid="generate-webhook-token">
            <Key className="h-3.5 w-3.5 mr-1" />
            Generate Webhook Token
          </Button>
        )}
        {token && (
          <Button variant="outline" size="sm" onClick={() => generateToken.mutate()}
            disabled={generateToken.isPending} className="text-xs" data-testid="regenerate-webhook-token">
            <RefreshCw className="h-3 w-3 mr-1" />
            Regenerate Token
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminAutomation() {
  const { toast } = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AutomationRule | null>(null);
  const [selectedRuleId, setSelectedRuleId] = useState<number | null>(null);
  const [expandedRunId, setExpandedRunId] = useState<number | null>(null);
  const [runSearch, setRunSearch] = useState("");
  const [deleteConfirmRule, setDeleteConfirmRule] = useState<AutomationRule | null>(null);

  const { data: rules = [], isLoading } = useQuery<AutomationRule[]>({
    queryKey: ["/api/admin/automation/rules"],
  });

  const { data: runs = [] } = useQuery<any[]>({
    queryKey: ["/api/admin/automation/runs"],
  });

  const { data: ruleRuns = [] } = useQuery<AutomationRun[]>({
    queryKey: ["/api/admin/automation/rules", selectedRuleId, "runs"],
    enabled: !!selectedRuleId,
  });

  const createRule = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/admin/automation/rules", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/automation/rules"] });
      setCreateOpen(false);
      toast({ title: "Automation rule created" });
    },
  });

  const updateRule = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => apiRequest("PUT", `/api/admin/automation/rules/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/automation/rules"] });
      setEditingRule(null);
      toast({ title: "Rule updated" });
    },
  });

  const deleteRule = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/admin/automation/rules/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/automation/rules"] });
      toast({ title: "Rule deleted" });
    },
  });

  const testRule = useMutation({
    mutationFn: (id: number) => apiRequest("POST", `/api/admin/automation/rules/${id}/test`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/automation/rules"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/automation/runs"] });
      toast({ title: "Test run completed" });
    },
    onError: () => {
      toast({ title: "Test run failed", variant: "destructive" });
    },
  });

  const toggleRule = (rule: AutomationRule) => {
    updateRule.mutate({ id: rule.id, data: { enabled: !rule.enabled } });
  };

  const activeRules = rules.filter(r => r.enabled);
  const inactiveRules = rules.filter(r => !r.enabled);
  const displayRuns = selectedRuleId ? ruleRuns : runs;
  const filteredRuns = runSearch
    ? displayRuns.filter((r: any) => {
        const name = (r as any).ruleName || rules.find(rl => rl.id === r.ruleId)?.name || "";
        return name.toLowerCase().includes(runSearch.toLowerCase()) ||
          r.status?.toLowerCase().includes(runSearch.toLowerCase()) ||
          JSON.stringify(r.result || {}).toLowerCase().includes(runSearch.toLowerCase());
      })
    : displayRuns;

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 space-y-6" data-testid="automation-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-testid="automation-title">
              <Zap className="h-6 w-6 text-[#C2652A]" />
              Automation
            </h1>
            <p className="text-sm text-gray-500 mt-1">Create workflow automations to streamline operations</p>
          </div>
          <Button className="bg-[#C2652A] hover:bg-[#A8531E]" onClick={() => setCreateOpen(true)} data-testid="new-rule-btn">
            <Plus className="h-4 w-4 mr-2" /> New Rule
          </Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                <Zap className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Total Rules</p>
                <p className="text-xl font-bold" data-testid="stat-total-rules">{rules.length}</p>
              </div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center">
                <Play className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Active</p>
                <p className="text-xl font-bold" data-testid="stat-active-rules">{activeRules.length}</p>
              </div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <Pause className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Inactive</p>
                <p className="text-xl font-bold" data-testid="stat-inactive-rules">{inactiveRules.length}</p>
              </div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                <Activity className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Total Runs</p>
                <p className="text-xl font-bold" data-testid="stat-total-runs">
                  {rules.reduce((sum, r) => sum + (r.runCount || 0), 0)}
                </p>
              </div>
            </div>
          </CardContent></Card>
        </div>

        <Tabs defaultValue="rules">
          <TabsList>
            <TabsTrigger value="rules" data-testid="tab-rules">Rules</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">
              Run History
              {runs.length > 0 && <Badge variant="secondary" className="ml-1.5 text-[10px] px-1.5">{runs.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="webhooks" data-testid="tab-webhooks">
              Webhooks
            </TabsTrigger>
          </TabsList>

          <TabsContent value="rules" className="space-y-3 mt-4">
            {isLoading ? (
              <div className="text-center py-12 text-gray-500">Loading rules...</div>
            ) : rules.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <div className="h-16 w-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
                    <Zap className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="font-semibold text-lg text-gray-600 dark:text-gray-300">No automation rules yet</h3>
                  <p className="text-sm text-gray-400 mt-1 mb-4">Create your first rule to automate workflows</p>
                  <Button className="bg-[#C2652A] hover:bg-[#A8531E]" onClick={() => setCreateOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" /> Create First Rule
                  </Button>
                </CardContent>
              </Card>
            ) : (
              rules.map(rule => {
                const triggerDef = triggerTypes.find(t => t.value === rule.triggerType);
                const TriggerIcon = triggerDef?.icon || Zap;
                const ruleActions = (Array.isArray(rule.actions) ? rule.actions : []) as { type: string; config?: Record<string, string> }[];
                const ruleConditions = rule.conditions as Record<string, string> | null;
                const hasConditions = ruleConditions && Object.keys(ruleConditions).filter(k => ruleConditions[k]).length > 0;

                return (
                  <Card key={rule.id} className={`transition-all ${!rule.enabled ? "opacity-60" : ""}`} data-testid={`rule-card-${rule.id}`}>
                    <CardContent className="p-4 sm:p-5">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            rule.enabled ? "bg-[#C2652A]/10 text-[#C2652A]" : "bg-gray-100 dark:bg-gray-800 text-gray-400"
                          }`}>
                            <TriggerIcon className="h-5 w-5" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="font-semibold text-sm" data-testid={`rule-name-${rule.id}`}>{rule.name}</h3>
                            {rule.description && <p className="text-xs text-gray-500 mt-0.5 truncate">{rule.description}</p>}
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <Badge variant="outline" className="text-xs">
                                <TriggerIcon className="h-3 w-3 mr-1" />
                                {triggerDef?.label || rule.triggerType}
                              </Badge>
                              {hasConditions && (
                                <Badge variant="outline" className="text-xs bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border-blue-200">
                                  <Filter className="h-3 w-3 mr-1" /> Filtered
                                </Badge>
                              )}
                              {ruleActions.length > 0 && (
                                <>
                                  <ArrowRight className="h-3 w-3 text-gray-400" />
                                  {ruleActions.slice(0, 3).map((a, i) => {
                                    const actionDef = actionTypes.find(at => at.value === a.type);
                                    const ActionIcon = actionDef?.icon || Settings;
                                    return (
                                      <Badge key={i} variant="secondary" className="text-xs">
                                        <ActionIcon className="h-3 w-3 mr-1" /> {actionDef?.label || a.type}
                                      </Badge>
                                    );
                                  })}
                                  {ruleActions.length > 3 && (
                                    <Badge variant="secondary" className="text-xs">+{ruleActions.length - 3} more</Badge>
                                  )}
                                </>
                              )}
                            </div>
                            {(rule.runCount > 0 || rule.lastRunAt) && (
                              <p className="text-[10px] text-gray-400 mt-1.5 flex items-center gap-1">
                                <Activity className="h-3 w-3" />
                                {rule.runCount} runs
                                {rule.lastRunAt && ` · Last: ${new Date(rule.lastRunAt).toLocaleString()}`}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 self-end sm:self-auto">
                          <Switch checked={rule.enabled} onCheckedChange={() => toggleRule(rule)} data-testid={`toggle-rule-${rule.id}`} />
                          <Button variant="ghost" size="sm" onClick={() => testRule.mutate(rule.id)}
                            disabled={testRule.isPending} data-testid={`test-rule-${rule.id}`}
                            title="Test run this rule">
                            <TestTube2 className="h-4 w-4 text-blue-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setEditingRule(rule)} data-testid={`edit-rule-${rule.id}`}>
                            <Edit className="h-4 w-4 text-gray-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setSelectedRuleId(selectedRuleId === rule.id ? null : rule.id)}
                            data-testid={`view-runs-${rule.id}`}>
                            <Clock className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmRule(rule)} data-testid={`delete-rule-${rule.id}`}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    className="pl-8 h-8 text-xs"
                    placeholder="Search runs by rule name, status, or result..."
                    value={runSearch}
                    onChange={e => setRunSearch(e.target.value)}
                    data-testid="input-run-search"
                  />
                </div>
                {selectedRuleId && (
                  <Button variant="outline" size="sm" onClick={() => setSelectedRuleId(null)} data-testid="clear-filter-btn">
                    Clear Filter
                  </Button>
                )}
              </div>
              {selectedRuleId && (
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-sm">
                    Filtered: {rules.find(r => r.id === selectedRuleId)?.name}
                  </h3>
                </div>
              )}
              {filteredRuns.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center py-12 text-gray-400">
                    <Activity className="h-8 w-8 mb-2" />
                    <p className="text-sm font-medium">No automation runs yet</p>
                    <p className="text-xs mt-1">Runs will appear here when automations are triggered</p>
                  </CardContent>
                </Card>
              ) : (
                filteredRuns.map((run: any) => {
                  const isExpanded = expandedRunId === run.id;
                  const resultData = run.result as any;
                  const payloadData = run.payload as any;

                  return (
                    <Card key={run.id} data-testid={`run-${run.id}`} className="cursor-pointer"
                      onClick={() => setExpandedRunId(isExpanded ? null : run.id)}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {run.status === "success" ? (
                              <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                            ) : run.status === "skipped" ? (
                              <Pause className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                            ) : (
                              <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                            )}
                            <div>
                              <p className="text-sm font-medium">
                                {(run as any).ruleName || rules.find(r => r.id === run.ruleId)?.name || `Rule #${run.ruleId}`}
                              </p>
                              <p className="text-xs text-gray-500">
                                {run.ranAt ? new Date(run.ranAt).toLocaleString() : "Unknown"}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={run.status === "success" ? "default" : run.status === "skipped" ? "secondary" : "destructive"}
                              className="text-xs capitalize">{run.status}</Badge>
                            {run.triggeredBy && (
                              <Badge variant="outline" className="text-xs">{run.triggeredBy}</Badge>
                            )}
                            {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="mt-3 pt-3 border-t space-y-2">
                            {payloadData && Object.keys(payloadData).length > 0 && (
                              <div>
                                <p className="text-xs font-medium text-muted-foreground mb-1">Trigger Data</p>
                                <pre className="text-xs bg-muted p-2 rounded overflow-x-auto max-h-32" data-testid={`run-payload-${run.id}`}>
                                  {JSON.stringify(payloadData, null, 2)}
                                </pre>
                              </div>
                            )}
                            {resultData && (
                              <div>
                                <p className="text-xs font-medium text-muted-foreground mb-1">Result</p>
                                {Array.isArray(resultData) ? (
                                  <div className="space-y-1">
                                    {resultData.map((r: any, i: number) => (
                                      <div key={i} className="flex items-start gap-2 text-xs p-1.5 bg-muted rounded">
                                        {r.status === "success" ? (
                                          <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                                        ) : (
                                          <XCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                                        )}
                                        <div className="min-w-0 flex-1">
                                          <span className="font-medium">{r.action || r.type}</span>
                                          {r.message && <span className="text-muted-foreground ml-1">- {r.message}</span>}
                                          {r.error && <span className="text-red-500 ml-1">- {r.error}</span>}
                                          {r.webhookStatus && (
                                            <Badge variant="outline" className="text-[10px] ml-1.5">
                                              HTTP {r.webhookStatus}
                                            </Badge>
                                          )}
                                          {r.webhookResponse && (
                                            <pre className="text-[10px] mt-1 bg-background p-1 rounded overflow-x-auto max-h-20 text-muted-foreground">
                                              {typeof r.webhookResponse === "string" ? r.webhookResponse : JSON.stringify(r.webhookResponse, null, 2)}
                                            </pre>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <pre className="text-xs bg-muted p-2 rounded overflow-x-auto max-h-32">
                                    {JSON.stringify(resultData, null, 2)}
                                  </pre>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </TabsContent>

          <TabsContent value="webhooks" className="mt-4">
            <WebhookTokenSection />
          </TabsContent>
        </Tabs>

        <RuleFormDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          initialData={emptyForm}
          onSubmit={data => createRule.mutate(data)}
          isPending={createRule.isPending}
          title="Create Automation Rule"
        />

        {editingRule && (
          <RuleFormDialog
            open={!!editingRule}
            onOpenChange={v => { if (!v) setEditingRule(null); }}
            initialData={{
              name: editingRule.name,
              description: editingRule.description || "",
              triggerType: editingRule.triggerType,
              conditions: (editingRule.conditions as Record<string, string>) || {},
              actions: (Array.isArray(editingRule.actions) ? editingRule.actions : []) as any,
              enabled: editingRule.enabled,
            }}
            onSubmit={data => updateRule.mutate({ id: editingRule.id, data })}
            isPending={updateRule.isPending}
            title="Edit Automation Rule"
          />
        )}
      </div>

      <DeleteConfirmDialog
        open={deleteConfirmRule !== null}
        onOpenChange={open => { if (!open) setDeleteConfirmRule(null); }}
        title="Delete this automation rule?"
        description={deleteConfirmRule ? <>This will permanently delete the automation rule <strong>"{deleteConfirmRule.name}"</strong>. Any future triggers will stop running. Historical run logs will remain. This cannot be undone.</> : null}
        confirmLabel="Delete Rule"
        pending={deleteRule.isPending}
        onConfirm={() => { if (deleteConfirmRule) { deleteRule.mutate(deleteConfirmRule.id); setDeleteConfirmRule(null); } }}
      />
    </AdminLayout>
  );
}
